import { Button as CMSDSButton, ButtonProps as CMSDSButtonProps } from '@cmsgov/design-system';
import classNames from 'classnames';
import { forwardRef, ReactNode } from 'react';

import './Button.css';

export enum ButtonVariation {
  SOLID = 'solid',
  GHOST = 'ghost',
}

export type ButtonProps = Omit<CMSDSButtonProps, 'variation' | 'children'> & {
  /**
   * Whether the button should take the full width of its container
   */
  isFullWidth?: boolean;
  /**
   * Whether the button only contains an icon (removes text padding)
   */
  isIconOnly?: boolean;
  /**
   * Whether the button should auto-size to its content
   */
  isAutoWidth?: boolean;
  /**
   * Whether the button is in a loading state
   */
  isLoading?: boolean;
  /**
   * Text to display when loading (if empty, shows spinner only)
   */
  loadingText?: string;
  /**
   * Whether to show a bordered/outline style
   */
  bordered?: boolean;
  /**
   * Visual style variation of the button
   */
  variation?: ButtonVariation;
  /**
   * Button content (text, icons, etc.)
   */
  children?: ReactNode;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
};

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      className,
      size,
      bordered = false,
      isFullWidth = false,
      isIconOnly = false,
      isAutoWidth = false,
      isLoading = false,
      loadingText = '',
      disabled,
      variation,
      children,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const buttonClassName = classNames(
      'access-button',
      {
        'access-button--auto-width': isAutoWidth,
        'access-button--big': size === 'big',
        'access-button--bordered': bordered,
        'access-button--full-width': isFullWidth,
        'access-button--icon-only': isIconOnly,
        'access-button--loading': isLoading,
        'access-button--small': size === 'small',
      },
      className,
    );

    const showSpinnerOnly = isLoading && loadingText === '';
    const baseTestId = dataTestId ?? 'button';

    const buttonContent = isLoading ? (
      showSpinnerOnly ? (
        <span
          className='access-button__loading-wrapper'
          data-testid={`${baseTestId}-loading`}>
          <span
            aria-hidden='true'
            className='access-button__spinner access-button__spinner--centered'
            data-testid={`${baseTestId}-spinner`}
          />
          <span
            className='access-button__hidden-content'
            data-testid={`${baseTestId}-hidden-content`}>
            {children}
          </span>
        </span>
      ) : (
        <>
          <span
            aria-hidden='true'
            className='access-button__spinner'
            data-testid={`${baseTestId}-spinner`}
          />
          <span data-testid={`${baseTestId}-loading-text`}>{loadingText ?? children}</span>
        </>
      )
    ) : (
      children
    );

    return (
      <CMSDSButton
        className={buttonClassName}
        data-testid={baseTestId}
        disabled={disabled || isLoading}
        inputRef={ref}
        size={size}
        variation={variation}
        {...props}>
        {buttonContent}
      </CMSDSButton>
    );
  },
);

Button.displayName = 'Button';
